import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, TimerReset } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";

/**
 * The payment deadline, from the outside: is the job running, what is waiting
 * for it, and a way to run it now. Added 23.09.2026, when three unpaid
 * enrolments stayed put and nothing on screen said why.
 */
export default function PaymentDeadlineCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/enrollments/payment-expiry/status");
      setState(r.data);
    } catch (err) {
      setState({ error: apiErrorMessage(err, "Nu am putut citi starea.") });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/enrollments/payment-expiry/run");
      const n = r.data.expired || 0;
      if (n) toast.success(`${n} ${n === 1 ? "înscriere închisă" : "înscrieri închise"}; locurile s-au eliberat.`);
      else if (r.data.due) toast.warning("Nimic nu s-a închis. Vezi motivul de mai jos.");
      else toast.message("Nicio înscriere nu a depășit termenul.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Verificarea nu a rulat."));
    } finally {
      setBusy(false);
    }
  };

  const last = state?.last_run;
  // Grouped by what happened AND why: "EuPlătesc nu a răspuns" was the same
  // sentence whether their service was down or simply had no such payment,
  // and the two need different answers from us (24.09.2026).
  const skipped = (last?.skipped || []).reduce((acc, s) => {
    const key = `${s.skipped}|${s.reason || ""}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const REASONS = {
    paid: "plătite între timp (nu se închid)",
    unreachable: "EuPlătesc nu a răspuns; se reîncearcă",
    status_changed: "statusul s-a schimbat între timp",
  };

  return (
    <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="payment-deadline-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-[#373A45]">Termen de plată</h3>
        <button type="button" onClick={runNow} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-4 py-1.5 text-sm font-semibold hover:bg-[#F5F6F6] disabled:opacity-60"
          data-testid="payment-deadline-run">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <TimerReset size={13} />} Verifică acum
        </button>
      </div>
      <p className="mt-1 text-xs text-[#6B7280]">
        Înscrierile neplătite în {state?.hours || 48} de ore de la începerea plății se închid, locul se eliberează,
        iar persoana trece la Leaduri. La grupele cu cel mult {state?.scarce_seats || 3} locuri libere termenul e
        de {state?.scarce_hours || 24} de ore, pentru că acolo locul e rar. Verificarea automată rulează la fiecare
        30 de minute.
      </p>

      {state?.error && <p className="mt-2 text-xs text-[#9b1f2c]">{state.error}</p>}
      {state && !state.error && (
        <div className="mt-3 space-y-1 text-xs text-[#373A45]" data-testid="payment-deadline-state">
          <div>
            Așteaptă închiderea acum: <strong className="tabular-nums">{state.due_now}</strong>
          </div>
          <div>
            Ultima verificare:{" "}
            {last?.at
              ? <strong>{new Date(last.at).toLocaleString("ro-RO")}</strong>
              : <span className="text-[#8a6a1a]">niciodată — jobul automat nu a rulat încă</span>}
            {last && <> · închise: <strong className="tabular-nums">{last.expired || 0}</strong></>}
          </div>
          {last?.error && <div className="text-[#9b1f2c]">Eroare la ultima rulare: {last.error}</div>}
          {Object.entries(skipped).map(([key, count]) => {
            const [what, why] = key.split("|");
            return (
              <div key={key} className="text-[#8a6a1a]">
                {count} neînchise: {REASONS[what] || what}
                {why ? <span className="text-[#6B7280]"> ({why})</span> : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
